import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Snackbar,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  SportsTennis,
  PersonAdd,
  ContentPaste,
  TableChart,
  ArrowBack,
  ExpandMore,
  ExpandLess,
  CloudDownload,
} from '@mui/icons-material';
import {
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/common/AppHeader';
import TennisPoolImportDialog from '../components/TennisPoolImportDialog';
import TennisExcelImportDialog from '../components/TennisExcelImportDialog';
import { tennisPlayerPoolService } from '../services/firestore';
import type { PlayerPool, PlayerPoolEntry, TennisScoringConfig, TennisMatchPerformance } from '../types/database';
import { DEFAULT_TENNIS_SCORING_CONFIG, calculateTennisMatchPoints } from '../utils/pointsCalculation';
import { deriveSetFlags, filterImportCandidatesByCutoff, type SetScore } from '../utils/tennisMatchPerformance';
import { flagEmoji } from '../utils/countryFlags';

// Render a performance's set scores as "6-4, 6-3" from the player's perspective.
const formatPerfScore = (perf: TennisMatchPerformance): string =>
  perf.setScores.length === 0
    ? '—'
    : perf.setScores.map(s => `${s.gamesWon}-${s.gamesLost}`).join(', ');

const TennisPlayerPoolManagementPage: React.FC = () => {
  const [playerPools, setPlayerPools] = useState<PlayerPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState<PlayerPool | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState<string>('');

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadPools = async () => {
      try {
        setLoading(true);
        setError('');
        const pools = await tennisPlayerPoolService.getAll();
        setPlayerPools(pools.map(p => ({ ...p, players: p.players || [] })));
      } catch (err: any) {
        console.error('Error loading tennis player pools:', err);
        setError(err?.message || 'Failed to load tennis player pools. Check Firestore permissions.');
      } finally {
        setLoading(false);
      }
    };
    loadPools();
  }, []);

  if (loading) {
    return (
      <Box>
        <AppHeader />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader />

      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box mb={{ xs: 2, sm: 3, md: 4 }} display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={{ xs: 1.5, sm: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Tooltip title="Back to Cricket Pools">
                <IconButton size="small" onClick={() => navigate('/admin/player-pools')}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                Tennis Player Pools
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Manage tennis player pools for tournament draws
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<Add />}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Create Tennis Pool
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Left Panel - Pool List */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Tennis Pools ({playerPools.length})
                </Typography>

                {playerPools.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                    <SportsTennis sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      No tennis pools created yet
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setCreateDialogOpen(true)}
                      sx={{ mt: 2 }}
                      startIcon={<Add />}
                    >
                      Create First Pool
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, mt: { xs: 1.5, sm: 2 } }}>
                    {playerPools.map((pool) => (
                      <Card
                        key={pool.id}
                        sx={{
                          cursor: 'pointer',
                          border: selectedPool?.id === pool.id ? '2px solid' : '1px solid',
                          borderColor: selectedPool?.id === pool.id ? 'primary.main' : 'divider',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={async () => {
                          // Re-fetch via getById so player.tennisMatchPerformances
                          // is hydrated from the subcollection (the list-of-pools
                          // getAll() call only returns slim parent docs).
                          const hydrated = await tennisPlayerPoolService.getById(pool.id);
                          setSelectedPool(hydrated ?? pool);
                        }}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {pool.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pool.players.length} players
                              </Typography>
                            </Box>
                            <Chip
                              label={pool.isActive ? 'Active' : 'Inactive'}
                              color={pool.isActive ? 'success' : 'default'}
                              size="small"
                              sx={{ flexShrink: 0 }}
                            />
                          </Box>
                          {pool.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                              {pool.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Pool Details */}
          <Grid size={{ xs: 12, md: 8 }}>
            {selectedPool ? (
              <TennisPoolDetails
                pool={selectedPool}
                onUpdate={async (updatedPool) => {
                  try {
                    // Persist the FULL players array — covers adds/removes/edits/imports.
                    // (The older path used `updatePlayerPoints` which only mutates existing
                    // entries by id, so newly-added players were silently dropped.)
                    // Only include `lastUpdateMessage` when it's set — Firestore rejects
                    // `undefined` field values via `updateDoc`.
                    const updates: Partial<PlayerPool> = { players: updatedPool.players };
                    if (updatedPool.lastUpdateMessage) {
                      updates.lastUpdateMessage = updatedPool.lastUpdateMessage;
                    }
                    await tennisPlayerPoolService.update(updatedPool.id, updates);

                    // Push any point deltas down to squads + leaderboards.
                    // Safe for new entries: cascadeUpdateToLeagues no-ops for player IDs
                    // that don't appear in any squad yet.
                    const pointsUpdates = updatedPool.players.map((p) => ({
                      playerId: p.playerId,
                      points: p.points,
                    }));
                    await tennisPlayerPoolService.cascadeUpdateToLeagues(updatedPool.id, pointsUpdates);

                    // Re-fetch via getById so player.tennisMatchPerformances stays
                    // hydrated from the subcollection after this metadata update.
                    const fresh = await tennisPlayerPoolService.getById(updatedPool.id);
                    const finalPool = fresh ?? updatedPool;
                    setPlayerPools(prev => prev.map(p => p.id === finalPool.id ? finalPool : p));
                    setSelectedPool(finalPool);
                    setSnackbarMessage('Tennis pool updated successfully!');
                    setSnackbarOpen(true);
                  } catch (err: any) {
                    console.error('Error updating tennis pool:', err);
                    setSnackbarMessage(`Failed to update: ${err?.message || 'Unknown error'}`);
                    setSnackbarOpen(true);
                  }
                }}
                onDelete={async () => {
                  try {
                    await tennisPlayerPoolService.delete(selectedPool.id);
                    setPlayerPools(prev => prev.filter(p => p.id !== selectedPool.id));
                    setSelectedPool(null);
                    setSnackbarMessage('Tennis pool deleted.');
                    setSnackbarOpen(true);
                  } catch (err) {
                    console.error('Error deleting tennis pool:', err);
                  }
                }}
                onReload={async () => {
                  const fresh = await tennisPlayerPoolService.getById(selectedPool.id);
                  if (fresh) {
                    const norm = { ...fresh, players: fresh.players || [] };
                    setPlayerPools(prev => prev.map(p => p.id === norm.id ? norm : p));
                    setSelectedPool(norm);
                  }
                }}
              />
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: { xs: 5, sm: 6, md: 8 } }}>
                  <SportsTennis sx={{ fontSize: { xs: 60, sm: 70, md: 80 }, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Select a Tennis Pool
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a pool from the list to view and manage players
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Create Pool Dialog */}
      <CreateTennisPoolDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={async (newPool) => {
          try {
            const poolId = await tennisPlayerPoolService.create({
              name: newPool.name,
              description: newPool.description,
              format: 'Tennis',
              scoringMode: 'manual',
              creatorId: user?.uid || '',
              adminIds: [user?.uid || ''],
              players: [],
              tennisConfig: newPool.tennisConfig,
              isActive: true,
            });
            const created: PlayerPool = { ...newPool, id: poolId, players: [] };
            setPlayerPools(prev => [...prev, created]);
            setCreateDialogOpen(false);
            setSelectedPool(created);
            setSnackbarMessage(`Pool "${newPool.name}" created!`);
            setSnackbarOpen(true);
          } catch (err) {
            console.error('Error creating tennis pool:', err);
            setSnackbarMessage('Failed to create pool. Please try again.');
            setSnackbarOpen(true);
          }
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tennis Pool Details Sub-Component
// ─────────────────────────────────────────────────────────────────────────────

const TennisPoolDetails: React.FC<{
  pool: PlayerPool;
  onUpdate: (pool: PlayerPool) => Promise<void>;
  onDelete: () => Promise<void>;
  onReload: () => Promise<void>;
}> = ({ pool, onUpdate, onDelete, onReload }) => {
  const { user } = useAuth();
  const [players, setPlayers] = useState<PlayerPoolEntry[]>(pool.players || []);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [editPlayerOpen, setEditPlayerOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerPoolEntry | null>(null);
  const [tennisImportOpen, setTennisImportOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [addMatchOpen, setAddMatchOpen] = useState(false);
  const [matchPlayer, setMatchPlayer] = useState<PlayerPoolEntry | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [editPointsOpen, setEditPointsOpen] = useState(false);

  const config = pool.tennisConfig ?? DEFAULT_TENNIS_SCORING_CONFIG;

  useEffect(() => {
    setPlayers(pool.players || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);

  const savePool = async (updatedPlayers: PlayerPoolEntry[], message?: string) => {
    await onUpdate({ ...pool, players: updatedPlayers, lastUpdateMessage: message });
  };

  const handleDeletePlayer = async (playerId: string) => {
    const updated = players.filter(p => p.playerId !== playerId);
    setPlayers(updated);
    await savePool(updated);
    setSnackbarMessage('Player removed.');
    setSnackbarOpen(true);
  };

  const handleEditPlayer = (player: PlayerPoolEntry) => {
    setSelectedPlayer(player);
    setEditPlayerOpen(true);
  };

  const handleSaveEditedPlayer = async (updated: PlayerPoolEntry) => {
    const updatedPlayers = players.map(p => p.playerId === updated.playerId ? updated : p);
    setPlayers(updatedPlayers);
    await savePool(updatedPlayers);
    setSnackbarMessage(`${updated.name} updated.`);
    setSnackbarOpen(true);
  };

  const handleAddMatch = async (perf: TennisMatchPerformance) => {
    if (!matchPlayer) return;
    await tennisPlayerPoolService.addMatchPerformance(
      pool.id,
      matchPlayer.playerId,
      perf,
      `${matchPlayer.name} — ${perf.round || 'match'} vs ${perf.opponentName || 'opponent'}`,
      user?.uid
    );
    await onReload();
    setAddMatchOpen(false);
    setMatchPlayer(null);
    setSnackbarMessage(`Match added for ${matchPlayer.name} (+${perf.pointsEarned} pts).`);
    setSnackbarOpen(true);
  };

  const handleDeleteMatch = async (playerId: string, perfId: string) => {
    if (!window.confirm('Delete this match record? Points will be recalculated.')) return;
    await tennisPlayerPoolService.deleteMatchPerformance(pool.id, playerId, perfId, user?.uid);
    await onReload();
    setSnackbarMessage('Match record deleted.');
    setSnackbarOpen(true);
  };

  const menPlayers = players.filter(p => p.gender === 'men' || p.role === 'men');
  const womenPlayers = players.filter(p => p.gender === 'women' || p.role === 'women');
  const ungrouped = players.filter(p => !p.gender && p.role !== 'men' && p.role !== 'women');

  const renderMatchHistory = (player: PlayerPoolEntry) => {
    const records = player.tennisMatchPerformances || [];
    return (
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={expandedPlayerId === player.playerId} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Match History ({records.length})
              </Typography>
              {records.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No matches recorded yet. Use "Add Match" or "Import from API".
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Round</TableCell>
                        <TableCell>Opponent</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell align="center">Result</TableCell>
                        <TableCell align="right">Aces</TableCell>
                        <TableCell align="right">DF</TableCell>
                        <TableCell align="right">Points</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map(r => (
                        <TableRow key={r.id}>
                          <TableCell>{r.round || '—'}</TableCell>
                          <TableCell>
                            {r.opponentName || '—'}
                            {r.opponentSeed !== undefined ? ` [${r.opponentSeed}]` : ''}
                          </TableCell>
                          <TableCell>{formatPerfScore(r)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={r.won ? 'Won' : 'Lost'}
                              size="small"
                              color={r.won ? 'success' : 'default'}
                              variant={r.won ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="right">{r.aces ?? '—'}</TableCell>
                          <TableCell align="right">{r.doubleFaults ?? '—'}</TableCell>
                          <TableCell align="right"><strong>{r.pointsEarned}</strong></TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleDeleteMatch(player.playerId, r.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderPlayerGroup = (label: string, group: PlayerPoolEntry[]) => {
    if (group.length === 0) return null;
    return (
      <React.Fragment key={label}>
        <TableRow sx={{ bgcolor: 'action.hover' }}>
          <TableCell colSpan={7}>
            <Typography variant="subtitle2" fontWeight="bold">{label} ({group.length})</Typography>
          </TableCell>
        </TableRow>
        {group.map(player => {
          const matchCount = player.tennisMatchPerformances?.length || 0;
          return (
            <React.Fragment key={player.playerId}>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedPlayerId(expandedPlayerId === player.playerId ? null : player.playerId)}
                    >
                      {expandedPlayerId === player.playerId ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                    {player.name}
                  </Box>
                </TableCell>
                <TableCell>{flagEmoji(player.team)} {player.team}</TableCell>
                <TableCell>
                  <Chip
                    label={player.seed ? `Seed ${player.seed}` : 'Unseeded'}
                    size="small"
                    color={player.seed ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {player.price !== undefined ? (
                    <Chip label={`${player.price}cr`} size="small" color="primary" variant="outlined" />
                  ) : (
                    <Chip label="—" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">{player.points}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={matchCount} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Add Match">
                      <IconButton size="small" color="primary" onClick={() => { setMatchPlayer(player); setAddMatchOpen(true); }}>
                        <SportsTennis fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleEditPlayer(player)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeletePlayer(player.playerId)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
              {renderMatchHistory(player)}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <Card>
      <CardContent>
        {/* Pool header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>{pool.name}</Typography>
            {pool.description && (
              <Typography variant="body2" color="text.secondary">{pool.description}</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label={`${players.length} players`} size="small" />
              <Chip label={pool.isActive ? 'Active' : 'Inactive'} color={pool.isActive ? 'success' : 'default'} size="small" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Total points are the sum of each player's recorded matches.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={() => setImportOpen(true)} startIcon={<CloudDownload />} size="small">
              Import from API
            </Button>
            <Button variant="outlined" onClick={() => setAddPlayerOpen(true)} startIcon={<PersonAdd />} size="small">
              Add Player
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => setBulkImportOpen(true)} startIcon={<ContentPaste />} size="small">
              Bulk Import Players
            </Button>
            <Button variant="outlined" color="primary" onClick={() => setTennisImportOpen(true)} startIcon={<ContentPaste />} size="small">
              Import from Draw
            </Button>
            <Button variant="outlined" onClick={() => setExcelImportOpen(true)} startIcon={<TableChart />} size="small">
              Upload Excel
            </Button>
            <Button variant="outlined" color="success" onClick={() => setEditPointsOpen(true)} startIcon={<Edit />} size="small">
              Edit Points
            </Button>
            <Button variant="outlined" color="error" onClick={() => setDeleteConfirmOpen(true)} startIcon={<Delete />} size="small">
              Delete Pool
            </Button>
          </Box>
        </Box>

        {/* Players Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Country</strong></TableCell>
                <TableCell><strong>Seed</strong></TableCell>
                <TableCell align="right"><strong>Price</strong></TableCell>
                <TableCell align="right"><strong>Points</strong></TableCell>
                <TableCell align="right"><strong>Matches</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No players yet</Typography>
                      <Button variant="outlined" onClick={() => setAddPlayerOpen(true)} sx={{ mt: 2 }} startIcon={<PersonAdd />}>
                        Add First Player
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {renderPlayerGroup("Men's Singles", menPlayers)}
                  {renderPlayerGroup("Women's Singles", womenPlayers)}
                  {renderPlayerGroup('Players', ungrouped)}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialogs */}
        {matchPlayer && (
          <TennisAddMatchDialog
            open={addMatchOpen}
            player={matchPlayer}
            config={config}
            currentUserId={user?.uid}
            onClose={() => { setAddMatchOpen(false); setMatchPlayer(null); }}
            onAdd={handleAddMatch}
          />
        )}

        <TennisImportPerformancesDialog
          open={importOpen}
          poolId={pool.id}
          players={players}
          onClose={() => setImportOpen(false)}
          onImport={async (candidates, message) => {
            await tennisPlayerPoolService.importMatchPerformances(
              pool.id,
              candidates,
              message || 'Imported match results from API',
              user?.uid
            );
            await onReload();
            const total = candidates.reduce((s, c) => s + c.performances.length, 0);
            setImportOpen(false);
            setSnackbarMessage(`${total} match record(s) imported.`);
            setSnackbarOpen(true);
          }}
        />

        <TennisAddPlayerDialog
          open={addPlayerOpen}
          onClose={() => setAddPlayerOpen(false)}
          onAdd={async (player) => {
            const updated = [...players, { ...player, lastUpdated: new Date() }];
            setPlayers(updated);
            await savePool(updated);
            setAddPlayerOpen(false);
            setSnackbarMessage(`${player.name} added.`);
            setSnackbarOpen(true);
          }}
        />

        {selectedPlayer && (
          <TennisEditPlayerDialog
            open={editPlayerOpen}
            player={selectedPlayer}
            onClose={() => { setEditPlayerOpen(false); setSelectedPlayer(null); }}
            onSave={async (updated) => {
              setEditPlayerOpen(false);
              setSelectedPlayer(null);
              await handleSaveEditedPlayer(updated);
            }}
          />
        )}

        <TennisPoolImportDialog
          open={tennisImportOpen}
          onClose={() => setTennisImportOpen(false)}
          existingPlayerIds={players.map(p => p.playerId)}
          onImport={async (imported) => {
            const withTs = imported.map(p => ({ ...p, lastUpdated: new Date() }));
            const updated = [...players, ...withTs];
            setPlayers(updated);
            await savePool(updated);
            setSnackbarMessage(`${imported.length} players imported from draw.`);
            setSnackbarOpen(true);
          }}
        />

        <TennisExcelImportDialog
          open={excelImportOpen}
          onClose={() => setExcelImportOpen(false)}
          poolName={pool.name}
          existingPlayerIds={players.map(p => p.playerId)}
          onImport={async (imported) => {
            const withTs = imported.map(p => ({ ...p, lastUpdated: new Date() }));
            const updated = [...players, ...withTs];
            setPlayers(updated);
            await savePool(updated);
            setSnackbarMessage(`${imported.length} players imported from Excel.`);
            setSnackbarOpen(true);
          }}
        />

        <TennisBulkImportDialog
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          existingPlayerIds={players.map(p => p.playerId)}
          onImport={async (imported) => {
            const withTs = imported.map(p => ({ ...p, lastUpdated: new Date() }));
            const updated = [...players, ...withTs];
            setPlayers(updated);
            await savePool(updated);
            setBulkImportOpen(false);
            setSnackbarMessage(`${imported.length} players imported.`);
            setSnackbarOpen(true);
          }}
        />

        <TennisEditPointsDialog
          open={editPointsOpen}
          players={players}
          onClose={() => setEditPointsOpen(false)}
          onSave={async (updatedPlayers, message) => {
            setPlayers(updatedPlayers);
            await savePool(updatedPlayers, message || undefined);
            setEditPointsOpen(false);
            setSnackbarMessage('Points updated.');
            setSnackbarOpen(true);
          }}
        />

        {/* Delete pool confirmation */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Pool?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{pool.name}</strong>? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                setDeleteConfirmOpen(false);
                await onDelete();
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add Player Dialog (Tennis)
// ─────────────────────────────────────────────────────────────────────────────

const TennisAddPlayerDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onAdd: (player: PlayerPoolEntry) => Promise<void>;
}> = ({ open, onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [gender, setGender] = useState<'men' | 'women'>('men');
  const [seed, setSeed] = useState<number | ''>('');
  const [price, setPrice] = useState<number | ''>('');

  const reset = () => { setName(''); setCountry(''); setGender('men'); setSeed(''); setPrice(''); };

  const handleAdd = async () => {
    const player: PlayerPoolEntry = {
      playerId: Date.now().toString(),
      name,
      team: country,
      role: gender,
      gender,
      points: 0,
      isOverseas: false,
      lastUpdated: new Date(),
      ...(seed !== '' ? { seed } : {}),
      ...(price !== '' ? { price } : {}),
    };
    await onAdd(player);
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Tennis Player</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Player Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Country / Nationality"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Gender Category</InputLabel>
            <Select value={gender} label="Gender Category" onChange={(e) => setGender(e.target.value as 'men' | 'women')}>
              <MenuItem value="men">Men's Singles</MenuItem>
              <MenuItem value="women">Women's Singles</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Tournament Seed (optional)"
            type="number"
            value={seed}
            onChange={(e) => setSeed(e.target.value === '' ? '' : parseInt(e.target.value))}
            fullWidth
            helperText="Leave blank if unseeded"
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">cr</InputAdornment> }}
            helperText="Credits cost for budget leagues"
            inputProps={{ min: 1 }}
          />
          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            Points start at 0 and build up from recorded matches (Add Match / Import from API).
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained" disabled={!name.trim() || !country.trim()}>
          Add Player
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit Player Dialog (Tennis)
// ─────────────────────────────────────────────────────────────────────────────

const TennisEditPlayerDialog: React.FC<{
  open: boolean;
  player: PlayerPoolEntry;
  onClose: () => void;
  onSave: (player: PlayerPoolEntry) => Promise<void>;
}> = ({ open, player, onClose, onSave }) => {
  const [name, setName] = useState(player.name);
  const [country, setCountry] = useState(player.team);
  const [gender, setGender] = useState<'men' | 'women'>((player.gender || player.role) as 'men' | 'women' || 'men');
  const [seed, setSeed] = useState<number | ''>(player.seed ?? '');
  const [price, setPrice] = useState<number | ''>(player.price ?? '');

  useEffect(() => {
    setName(player.name);
    setCountry(player.team);
    setGender((player.gender || player.role) as 'men' | 'women' || 'men');
    setSeed(player.seed ?? '');
    setPrice(player.price ?? '');
  }, [player]);

  const handleSave = async () => {
    await onSave({
      ...player,
      name,
      team: country,
      role: gender,
      gender,
      ...(seed !== '' ? { seed } : { seed: undefined }),
      ...(price !== '' ? { price } : { price: undefined }),
      lastUpdated: new Date(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Player: {player.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField label="Player Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
          <TextField label="Country / Nationality" value={country} onChange={(e) => setCountry(e.target.value)} fullWidth required />
          <FormControl fullWidth>
            <InputLabel>Gender Category</InputLabel>
            <Select value={gender} label="Gender Category" onChange={(e) => setGender(e.target.value as 'men' | 'women')}>
              <MenuItem value="men">Men's Singles</MenuItem>
              <MenuItem value="women">Women's Singles</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Tournament Seed (optional)"
            type="number"
            value={seed}
            onChange={(e) => setSeed(e.target.value === '' ? '' : parseInt(e.target.value))}
            fullWidth
            helperText="Leave blank if unseeded"
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">cr</InputAdornment> }}
            inputProps={{ min: 1 }}
          />
          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            Points are the sum of recorded matches and can't be edited here. Use Add Match or Import from API.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Cancel />}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<Save />} disabled={!name.trim() || !country.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Create Tennis Pool Dialog
// ─────────────────────────────────────────────────────────────────────────────

const CreateTennisPoolDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (pool: PlayerPool) => Promise<void>;
}> = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tennisConfig, setTennisConfig] = useState<TennisScoringConfig>({ ...DEFAULT_TENNIS_SCORING_CONFIG });
  const { user } = useAuth();

  const reset = () => {
    setName('');
    setDescription('');
    setTennisConfig({ ...DEFAULT_TENNIS_SCORING_CONFIG });
  };

  const handleCreate = async () => {
    const newPool: PlayerPool = {
      id: Date.now().toString(),
      name,
      description,
      format: 'Tennis',
      scoringMode: 'manual',
      creatorId: user?.uid || '',
      adminIds: [user?.uid || ''],
      players: [],
      tennisConfig,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
    };
    await onSave(newPool);
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Tennis Player Pool</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '70vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Pool Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Roland Garros 2026"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of this tournament pool"
            />
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom color="primary">Tennis Scoring Rules</Typography>
            <Grid container spacing={2}>
              {([
                ['matchWinPoints',       'Match Win',                    'Base points for winning a match (+20)'],
                ['straightSetsBonus',    'Straight Sets Bonus',          'Won without dropping a set (+10)'],
                ['firstSetLossBonus',    'First Set Loss Bonus',         'Won after losing the first set (+10)'],
                ['tieBreakerBonus',      'Final Set Tie-Break Bonus',    'Final set decided by tie-break (+10)'],
                ['dominantSetPoints',    'Dominant Set (6-0 or 6-1)',    'Per set won 6-0 or 6-1 (+10)'],
                ['upsetSmallBonus',      'Upset ≥5 seeds higher',        'Beat player seeded 5+ places above (+8)'],
                ['upsetMediumBonus',     'Upset ≥10 seeds higher',       'Beat player seeded 10+ places above (+16)'],
                ['upsetLargeBonus',      'Major Upset ≥20 seeds higher', 'Beat player seeded 20+ places above (+24)'],
                ['mensPredictionBonus',  "Men's Winner Prediction",      "Correct men's singles winner pick (+20)"],
                ['womensPredictionBonus',"Women's Winner Prediction",    "Correct women's singles winner pick (+20)"],
              ] as [keyof TennisScoringConfig, string, string][]).map(([key, label, helperText]) => (
                <Grid key={key} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={label}
                    type="number"
                    value={(tennisConfig as any)[key] ?? 0}
                    onChange={(e) => setTennisConfig(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0 }}
                    helperText={helperText}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={!name.trim()}>
          Create Pool
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tennis Bulk Import Dialog
// Paste a plain text list of player names; gender detected from section headers
// ─────────────────────────────────────────────────────────────────────────────

const TennisBulkImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  existingPlayerIds: string[];
  onImport: (players: PlayerPoolEntry[]) => Promise<void>;
}> = ({ open, onClose, existingPlayerIds, onImport }) => {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<PlayerPoolEntry[]>([]);
  const [step, setStep] = useState<'paste' | 'review'>('paste');
  const [importing, setImporting] = useState(false);

  const handleClose = () => {
    setText('');
    setParsed([]);
    setStep('paste');
    onClose();
  };

  const parseText = () => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    let currentGender: 'men' | 'women' = 'men';
    const results: PlayerPoolEntry[] = [];

    for (const line of lines) {
      // Section headers
      if (/^(men'?s?|m:|males?)/i.test(line)) { currentGender = 'men'; continue; }
      if (/^(women'?s?|w:|ladies|females?)/i.test(line)) { currentGender = 'women'; continue; }
      // Skip separator lines
      if (/^[-=]+$/.test(line)) continue;

      // Parse: optional leading number/dot, then name
      // e.g. "1. Jannik Sinner", "Jannik Sinner", "M: Jannik Sinner"
      let name = line
        .replace(/^[mw]:\s*/i, '')
        .replace(/^\d+[.)]\s*/, '')
        .trim();
      if (!name) continue;

      const id = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (existingPlayerIds.includes(id)) continue;

      results.push({
        playerId: id,
        name,
        team: '',
        role: currentGender,
        gender: currentGender,
        points: 0,
        isOverseas: false,
        lastUpdated: new Date(),
      });
    }

    setParsed(results);
    setStep('review');
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await onImport(parsed);
    } finally {
      setImporting(false);
    }
  };

  const formatInstruction = `Paste player names one per line. Use section headers to set gender:\n\nMen's Singles\nJannik Sinner\nCarlos Alcaraz\n\nWomen's Singles\nIga Swiatek\nAryna Sabalenka\n\nYou can also prefix lines with M: or W:.`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Import Players</DialogTitle>
      <DialogContent dividers>
        {step === 'paste' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                {formatInstruction}
              </pre>
            </Alert>
            <TextField
              label="Player names"
              multiline
              rows={12}
              value={text}
              onChange={e => setText(e.target.value)}
              fullWidth
              placeholder={"Men's Singles\nJannik Sinner\nCarlos Alcaraz\n\nWomen's Singles\nIga Swiatek"}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity={parsed.length > 0 ? 'success' : 'warning'}>
              {parsed.length > 0
                ? `${parsed.length} new player(s) ready to import`
                : 'No new players found (all may already be in the pool).'}
            </Alert>
            {parsed.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Gender</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.map(p => (
                      <TableRow key={p.playerId}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>
                          <Chip label={p.gender === 'men' ? "Men's" : "Women's"} size="small" color={p.gender === 'men' ? 'primary' : 'secondary'} variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'paste' ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={parseText} disabled={!text.trim()}>
              Preview
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('paste')}>Back</Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={parsed.length === 0 || importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Import {parsed.length > 0 ? `${parsed.length} Players` : ''}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add Match Dialog (Tennis) — records one match performance for a player.
// Mirrors cricket's AddInningsDialog; reuses the set-score/outcome pattern from
// TennisMatchResultDialog. Detailed stats (aces, etc.) are stored for display
// only and do NOT affect the computed points.
// ─────────────────────────────────────────────────────────────────────────────

type TennisRound = 'R128' | 'R64' | 'R32' | 'R16' | 'QF' | 'SF' | 'Final';

const TennisAddMatchDialog: React.FC<{
  open: boolean;
  player: PlayerPoolEntry;
  config: TennisScoringConfig;
  currentUserId?: string;
  onClose: () => void;
  onAdd: (perf: TennisMatchPerformance) => Promise<void>;
}> = ({ open, player, config, currentUserId, onClose, onAdd }) => {
  const [round, setRound] = useState<TennisRound>('R64');
  const [opponentName, setOpponentName] = useState('');
  const [opponentSeed, setOpponentSeed] = useState<number | ''>('');
  const [won, setWon] = useState(true);
  const [opponentWithdrew, setOpponentWithdrew] = useState(false);
  const [setScores, setSetScores] = useState<SetScore[]>([{ gamesWon: 6, gamesLost: 4 }]);
  const [aces, setAces] = useState<number | ''>('');
  const [doubleFaults, setDoubleFaults] = useState<number | ''>('');
  const [firstServePct, setFirstServePct] = useState<number | ''>('');
  const [winners, setWinners] = useState<number | ''>('');
  const [unforcedErrors, setUnforcedErrors] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const isWomen = player.gender === 'women' || player.role === 'women';
  const maxSets = isWomen ? 3 : 5;

  const reset = () => {
    setRound('R64');
    setOpponentName('');
    setOpponentSeed('');
    setWon(true);
    setOpponentWithdrew(false);
    setSetScores([{ gamesWon: 6, gamesLost: 4 }]);
    setAces('');
    setDoubleFaults('');
    setFirstServePct('');
    setWinners('');
    setUnforcedErrors('');
  };

  const effectiveWon = won || opponentWithdrew;
  // Pass the player's gender so the final-set tie-break bonus is only awarded
  // when the tie-break was in the actual deciding set (set 5 men / set 3 women).
  const { straightSets, finalSetTieBreak } = deriveSetFlags(
    setScores,
    effectiveWon,
    opponentWithdrew,
    player.gender
  );
  const preview = calculateTennisMatchPoints(
    effectiveWon,
    straightSets,
    finalSetTieBreak,
    setScores.map(s => s.gamesWon),
    setScores.map(s => s.gamesLost),
    player.seed,
    opponentSeed !== '' ? opponentSeed : undefined,
    config
  );

  const addSet = () => setSetScores(prev => [...prev, { gamesWon: 6, gamesLost: 4 }]);
  const removeSet = (i: number) => setSetScores(prev => prev.filter((_, idx) => idx !== i));
  const updateSet = (i: number, field: keyof SetScore, value: number) =>
    setSetScores(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const handleAdd = async () => {
    setSaving(true);
    try {
      const perf: TennisMatchPerformance = {
        id: `${Date.now()}-${player.playerId}`,
        round,
        opponentName: opponentName.trim() || undefined,
        opponentSeed: opponentSeed !== '' ? opponentSeed : undefined,
        won: effectiveWon,
        setScores: opponentWithdrew ? [] : setScores,
        straightSets,
        finalSetTieBreak,
        aces: aces !== '' ? aces : undefined,
        doubleFaults: doubleFaults !== '' ? doubleFaults : undefined,
        firstServePct: firstServePct !== '' ? firstServePct : undefined,
        winners: winners !== '' ? winners : undefined,
        unforcedErrors: unforcedErrors !== '' ? unforcedErrors : undefined,
        pointsEarned: preview,
        source: 'manual',
        date: new Date(),
        addedBy: currentUserId,
      };
      await onAdd(perf);
      reset();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Match — {player.name}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Round</InputLabel>
                <Select value={round} label="Round" onChange={e => setRound(e.target.value as TennisRound)}>
                  {(['R128', 'R64', 'R32', 'R16', 'QF', 'SF', 'Final'] as TennisRound[]).map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField fullWidth size="small" label="Opponent Name" value={opponentName} onChange={e => setOpponentName(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Opp. Seed"
                value={opponentSeed}
                onChange={e => setOpponentSeed(e.target.value === '' ? '' : parseInt(e.target.value))}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={won} onChange={e => { setWon(e.target.checked); if (e.target.checked) setOpponentWithdrew(false); }} disabled={opponentWithdrew} />}
              label="Won"
            />
            <FormControlLabel
              control={<Checkbox checked={opponentWithdrew} onChange={e => setOpponentWithdrew(e.target.checked)} />}
              label="Opponent withdrew / walkover"
            />
            <Chip size="small" variant="outlined" label={isWomen ? '♀ Best of 3' : '♂ Best of 5'} sx={{ fontSize: '0.72rem' }} />
            {effectiveWon && (
              <>
                <Chip size="small" label={`Straight Sets: ${straightSets ? 'Yes' : 'No'}`} color={straightSets ? 'success' : 'default'} variant={straightSets ? 'filled' : 'outlined'} sx={{ fontSize: '0.72rem' }} />
                <Chip size="small" label={`Tie-Break: ${finalSetTieBreak ? 'Yes' : 'No'}`} color={finalSetTieBreak ? 'info' : 'default'} variant={finalSetTieBreak ? 'filled' : 'outlined'} sx={{ fontSize: '0.72rem' }} />
              </>
            )}
          </Box>

          {!opponentWithdrew && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Set Scores</Typography>
                <Button size="small" onClick={addSet} disabled={setScores.length >= maxSets}>+ Add Set</Button>
              </Box>
              {setScores.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 48 }}>Set {i + 1}</Typography>
                  <TextField size="small" type="number" label="Won" value={s.gamesWon} onChange={e => updateSet(i, 'gamesWon', parseInt(e.target.value) || 0)} inputProps={{ min: 0, max: 7 }} sx={{ width: 80 }} />
                  <Typography>–</Typography>
                  <TextField size="small" type="number" label="Lost" value={s.gamesLost} onChange={e => updateSet(i, 'gamesLost', parseInt(e.target.value) || 0)} inputProps={{ min: 0, max: 7 }} sx={{ width: 80 }} />
                  {setScores.length > 1 && <Button size="small" color="error" onClick={() => removeSet(i)}>Remove</Button>}
                </Box>
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Match Stats (optional — for display only)</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="Aces" value={aces} onChange={e => setAces(e.target.value === '' ? '' : parseInt(e.target.value))} inputProps={{ min: 0 }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="Double Faults" value={doubleFaults} onChange={e => setDoubleFaults(e.target.value === '' ? '' : parseInt(e.target.value))} inputProps={{ min: 0 }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="1st Serve %" value={firstServePct} onChange={e => setFirstServePct(e.target.value === '' ? '' : parseInt(e.target.value))} inputProps={{ min: 0, max: 100 }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="Winners" value={winners} onChange={e => setWinners(e.target.value === '' ? '' : parseInt(e.target.value))} inputProps={{ min: 0 }} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField fullWidth size="small" type="number" label="Unforced Errors" value={unforcedErrors} onChange={e => setUnforcedErrors(e.target.value === '' ? '' : parseInt(e.target.value))} inputProps={{ min: 0 }} />
              </Grid>
            </Grid>
          </Box>

          <Alert severity="info">
            Computed points: <strong>+{preview} pts</strong>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} startIcon={<Cancel />}>Cancel</Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          disabled={saving}
        >
          Add Match
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Import Performances Dialog (Tennis) — pulls finished matches from the synced
// tennisMatches data (via each entry's apiPlayerKey) and previews new records.
// ─────────────────────────────────────────────────────────────────────────────

const TennisImportPerformancesDialog: React.FC<{
  open: boolean;
  poolId: string;
  players: PlayerPoolEntry[];
  onClose: () => void;
  onImport: (
    candidates: {
      playerId: string;
      resolvedApiPlayerKey?: number;
      performances: TennisMatchPerformance[];
    }[],
    message: string
  ) => Promise<void>;
}> = ({ open, poolId, players, onClose, onImport }) => {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [tournamentFound, setTournamentFound] = useState(true);
  const [candidates, setCandidates] = useState<{
    playerId: string;
    resolvedApiPlayerKey?: number;
    performances: TennisMatchPerformance[];
  }[]>([]);
  const [matchedPlayerCount, setMatchedPlayerCount] = useState(0);
  const [unmatchedPlayerCount, setUnmatchedPlayerCount] = useState(0);
  const [updateMessage, setUpdateMessage] = useState('');
  const todayYMD = new Date().toISOString().slice(0, 10);
  const [cutoffDate, setCutoffDate] = useState(todayYMD);

  const nameById = new Map(players.map(p => [p.playerId, p.name]));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setCandidates([]);
    setMatchedPlayerCount(0);
    setUnmatchedPlayerCount(0);
    setUpdateMessage('');
    setCutoffDate(new Date().toISOString().slice(0, 10));
    tennisPlayerPoolService
      .fetchImportCandidates(poolId)
      .then(res => {
        setTournamentFound(res.tournamentFound);
        setCandidates(res.candidates);
        setMatchedPlayerCount(res.matchedPlayerCount);
        setUnmatchedPlayerCount(res.unmatchedPlayerCount);
      })
      .catch(err => setError(err?.message || 'Failed to load matches from API.'))
      .finally(() => setLoading(false));
  }, [open, poolId]);

  const filteredCandidates = filterImportCandidatesByCutoff(candidates, cutoffDate);
  const totalNew = filteredCandidates.reduce((s, c) => s + c.performances.length, 0);
  const totalAvailable = candidates.reduce((s, c) => s + c.performances.length, 0);

  const handleImport = async () => {
    setImporting(true);
    try {
      await onImport(filteredCandidates, updateMessage.trim());
    } catch (err: any) {
      setError(err?.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Match Results from API</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !tournamentFound ? (
          <Alert severity="warning">
            No tennis league linked to this pool has tournament keys set, so there are no synced
            matches to import. Set the tournament on the league, run the API sync, then try again.
          </Alert>
        ) : matchedPlayerCount === 0 ? (
          <Alert severity="warning">
            None of the {players.length} player(s) in this pool could be matched to the synced
            api-tennis players. Make sure an admin has already pressed{' '}
            <strong>Admin → Tennis Results → DO TENNIS API CALL</strong> for this tournament
            (that populates the player list this import reads from). Matching tries pool entries
            in any common form — &quot;Sinner Jannik&quot;, &quot;Jannik Sinner&quot;, or
            &quot;J. Sinner&quot; — against the api-tennis short form, so format alone usually
            isn&apos;t the problem.
          </Alert>
        ) : totalAvailable === 0 ? (
          <Alert severity="info">
            All finished matches for the {matchedPlayerCount} matched player(s) have already
            been imported.
            {unmatchedPlayerCount > 0 && (
              <> ({unmatchedPlayerCount} pool player(s) could not be matched.)</>
            )}
          </Alert>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Import matches up to"
                type="date"
                size="small"
                value={cutoffDate}
                onChange={(e) => setCutoffDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayYMD }}
                helperText="Only matches on or before this day will be imported"
              />
              <Typography variant="body2" color="text.secondary">
                {totalNew} of {totalAvailable} new record(s) within range
              </Typography>
            </Box>
            {totalNew === 0 ? (
              <Alert severity="info">
                No new matches fall on or before {cutoffDate}. Pick a later date to include more.
              </Alert>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {totalNew} new match record(s) ready to import across {filteredCandidates.length} player(s).
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Player</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Round</TableCell>
                        <TableCell>Opponent</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell align="center">Result</TableCell>
                        <TableCell align="right">Points</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCandidates.flatMap(c =>
                        c.performances.map(p => (
                          <TableRow key={`${c.playerId}-${p.id}`}>
                            <TableCell>{nameById.get(c.playerId) || c.playerId}</TableCell>
                            <TableCell>{(p.date instanceof Date ? p.date : new Date(p.date as any)).toISOString().slice(0, 10)}</TableCell>
                            <TableCell>{p.round || '—'}</TableCell>
                            <TableCell>{p.opponentName || '—'}</TableCell>
                            <TableCell>{formatPerfScore(p)}</TableCell>
                            <TableCell align="center">
                              <Chip label={p.won ? 'Won' : 'Lost'} size="small" color={p.won ? 'success' : 'default'} variant={p.won ? 'filled' : 'outlined'} />
                            </TableCell>
                            <TableCell align="right"><strong>{p.pointsEarned}</strong></TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TextField
                  label="Update Message (Optional)"
                  size="small"
                  fullWidth
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  placeholder="e.g. R3 results added"
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || importing || totalNew === 0}
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />}
        >
          Import {totalNew > 0 ? `${totalNew} Record(s)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TennisEditPointsDialog
// ─────────────────────────────────────────────────────────────────────────────

const TennisEditPointsDialog: React.FC<{
  open: boolean;
  players: PlayerPoolEntry[];
  onClose: () => void;
  onSave: (updatedPlayers: PlayerPoolEntry[], message: string) => Promise<void>;
}> = ({ open, players, onClose, onSave }) => {
  const [editedPoints, setEditedPoints] = useState<Record<string, number>>({});
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, number> = {};
    players.forEach(p => { initial[p.playerId] = p.points ?? 0; });
    setEditedPoints(initial);
    setMessage('');
  }, [open, players]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = players.map(p => ({ ...p, points: editedPoints[p.playerId] ?? p.points ?? 0 }));
      await onSave(updated, message.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Points</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Update Message (Optional)"
          size="small"
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Manual points correction"
          sx={{ mb: 2 }}
        />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Player</strong></TableCell>
                <TableCell align="right"><strong>Points</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.map(p => (
                <TableRow key={p.playerId}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={editedPoints[p.playerId] ?? p.points ?? 0}
                      onChange={(e) => setEditedPoints(prev => ({ ...prev, [p.playerId]: Number(e.target.value) }))}
                      inputProps={{ style: { textAlign: 'right', width: 80 } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TennisPlayerPoolManagementPage;
